import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  PaginationComponent,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@folio/components';
import { ErrorRow } from '@folio/components/module-elements/ErrorRow';
import { LoadingRow } from '@folio/components/module-elements/LoadingRow';
import { useCurrentPage } from '@folio/hooks/useCurrentPage';
import { useQuery } from '@tanstack/react-query';
import {
  legacyPermissionsControllerAtMeQueryOptions,
  viewSpecificControllerListChildBrokerageFirmsQueryOptions,
} from 'korint-sdk';
import { useKorintModuleConfig } from 'korint-sdk/react';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TeamCreationDialog } from './TeamCreationDialog';
import { TeamManagementRow } from './TeamManagementRow';

const PAGE_SIZE = 10;

export function TeamManagementModule() {
  const { t } = useTranslation();
  const [isCreateTeamDialogOpen, setIsCreateTeamDialogOpen] = useState(false);

  const teamManagementConfig = useKorintModuleConfig('teamManagement');

  const { data: user, isLoading: isUserLoading } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const currentPage = useCurrentPage();

  const {
    data: teams,
    status,
    refetch,
  } = useQuery({
    ...viewSpecificControllerListChildBrokerageFirmsQueryOptions(user?.brokerageFirmId!, {
      count: PAGE_SIZE,
      page: currentPage,
    }),
    enabled: !!user?.brokerageFirmId,
  });

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t('teamManagement.title')}</span>
            <Button
              onClick={() => setIsCreateTeamDialogOpen(true)}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              {t('teamManagement.addTeamButton')}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('teamManagement.columns.name')}</TableHead>
                {teamManagementConfig.columns?.map(column => (
                  <TableHead key={column.name}>
                    {column.label ?? t(`teamManagement.columns.${column.name}`)}
                  </TableHead>
                ))}
                <TableHead>{t('teamManagement.columns.createdAt')}</TableHead>
                <TableHead>{t('teamManagement.columns.products')}</TableHead>
                <TableHead>{t('teamManagement.columns.activeAccounts')}</TableHead>
                <TableHead>{t('teamManagement.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isUserLoading || status === 'pending' ? (
                Array.from({ length: PAGE_SIZE }).map((_, idx) => <LoadingRow key={idx} />)
              ) : status === 'error' ? (
                <ErrorRow />
              ) : (
                teams.data.map(team => (
                  <TeamManagementRow key={team.id} team={team} onRefetch={refetch} />
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
        <CardFooter>
          <PaginationComponent
            totalData={teams?.totalCount ?? 0}
            PAGE_SIZE={PAGE_SIZE}
            isLoading={status === 'pending'}
          />
        </CardFooter>
      </Card>

      <TeamCreationDialog
        isOpen={isCreateTeamDialogOpen}
        onClose={() => setIsCreateTeamDialogOpen(false)}
        onSuccess={() => {
          setIsCreateTeamDialogOpen(false);
          refetch();
        }}
      />
    </>
  );
}
